// DEV-3172: `/assets/*` is served with `Cache-Control: public, max-age=31536000`
// (netlify.toml), and Netlify's header rules can't condition on status, so a
// 404 for a chunk — the gap after a deploy, or a rollback to an older build —
// is stored in the browser's HTTP cache for a year. No reload clears it: a
// document reload revalidates only the document, the failing file is usually
// a *dependency* of the chunk named in the error, and ENG-1830's `?_cb=`
// retry changes only the entry chunk's URL. An edge function could set
// no-store on the 404, but Edge Functions are outside Netlify's BAA (#6831).
//
// `fetch(url, { cache: "reload" })` goes to the network and *overwrites* the
// cached entry — the one standard way to replace a single HTTP-cache entry.
// Verified in Chromium against a real Netlify deploy: a cached 404 kept
// failing across document reloads until one reload-mode fetch, after which the
// next import read a 200 from cache.

// Resource Timing's own buffer stops at 250 entries, which this SPA passes
// early, so failures are collected by an observer from boot instead.
const failedAssetUrls = new Set<string>();

// A rollback poisons a route's whole preloaded dependency graph at once
// (patients.routes alone has ~90 deps; 41 were cached as 404s in the dev-suno
// rollback test, where a cap of 30 left 11 poisoned and the page stuck). A
// build is ~350 JS/CSS files, so this bounds the work without ever being the
// reason a repair is incomplete. The time budget only bites when requests
// hang (e.g. offline); a real repair of dozens of files finishes in ~1s.
export const MAX_REPAIRS = 300;
export const REPAIR_TIMEOUT_MS = 10_000;

function toRepairableAssetUrl(rawUrl: string): string | undefined {
  try {
    const url = new URL(rawUrl, window.location.href);
    if (url.origin !== window.location.origin) return undefined;
    if (!url.pathname.startsWith("/assets/")) return undefined;
    // A `?_cb=` retry has its own cache key; the poisoned entry is the plain URL.
    url.searchParams.delete("_cb");
    return url.href;
  } catch {
    return undefined;
  }
}

export function rememberFailedAssetUrl(rawUrl: string | undefined) {
  const url = rawUrl && toRepairableAssetUrl(rawUrl);
  if (url) failedAssetUrls.add(url);
}

/** Starts recording failed `/assets/*` requests. Call once at boot. */
export function watchFailedAssetRequests() {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        // `responseStatus` isn't reported everywhere (Safari lacks it); there
        // the URL from the error message (rememberFailedAssetUrl) is the fallback.
        const { responseStatus } = entry as PerformanceResourceTiming & {
          responseStatus?: number;
        };
        if (responseStatus !== undefined && responseStatus >= 400) {
          rememberFailedAssetUrl(entry.name);
        }
      }
    });
    observer.observe({ type: "resource", buffered: true });
  } catch {
    // No PerformanceObserver — repair falls back to error-message URLs.
  }
}

// One chunk failure can reach reloadPage twice (the vite:preloadError handler
// and lazyWithRetry), so concurrent callers share a single repair pass.
let repairInFlight: Promise<void> | undefined;

/**
 * Re-fetches every recorded failed asset with `cache: "reload"` so the next
 * load reads the server's current answer instead of a cached 404. Never
 * rejects and never takes longer than REPAIR_TIMEOUT_MS; a file that is
 * genuinely gone just stays a 404.
 */
export function repairPoisonedAssetCache(): Promise<void> {
  if (!repairInFlight) {
    repairInFlight = runRepair().finally(() => {
      repairInFlight = undefined;
    });
  }
  return repairInFlight;
}

async function runRepair(): Promise<void> {
  const urls = [...failedAssetUrls].slice(0, MAX_REPAIRS);
  if (urls.length === 0) return;

  const repairs = urls.map(async (url) => {
    try {
      const response = await fetch(url, { cache: "reload" });
      if (response.ok) failedAssetUrls.delete(url);
    } catch {
      // Network failure — nothing cached to fix, the reload will retry.
    }
  });
  await Promise.race([
    Promise.all(repairs),
    new Promise((resolve) => setTimeout(resolve, REPAIR_TIMEOUT_MS))
  ]);
}
